import { useEffect, useRef, useState } from "react";
import Odometer from "react-odometerjs";
import { MdStar, MdStarBorder } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Col, Row } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { toGetStatistics } from "../../../apis/actions/statistics.actions";
import { toGetTestimonies } from "../../../apis/actions/testimonies.actions";
import { HOME_WOW_DURATION, homeWowDelay } from "../../../utils/homeWow";

function initialsFromName(name: string): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    const w = parts[0];
    return w.length >= 2 ? w.slice(0, 2).toUpperCase() : w.charAt(0).toUpperCase();
  }
  const a = parts[0].charAt(0);
  const b = parts[parts.length - 1].charAt(0);
  return (a + b).toUpperCase();
}

function clampRating(n: unknown): number {
  if (typeof n !== "number" || Number.isNaN(n)) return 5;
  return Math.min(5, Math.max(0, Math.round(n)));
}

const Says = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { list } = useSelector(
    (state: RootState) => state.statistics.statistics
  );
  const { list: data } = useSelector(
    (state: RootState) => state.testimonies.testimonies
  );

  const [state, setState] = useState({
    staisfaction: 0,
    nbCars: 0,
    nbClients: 0,
  });
  const blockRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(toGetStatistics(i18n.language));
    dispatch(toGetTestimonies(i18n.language));
  }, [dispatch, i18n.language]);

  useEffect(() => {
    const block = blockRef.current;

    const handleIntersect: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const timeoutId = setTimeout(() => {
            const newState = list.reduce(
              (acc: any, current: any) => {
                switch (current.name) {
                  case "Satisfaction Rate":
                    acc.staisfaction = Number(current.value);
                    break;
                  case "Number of cars":
                    acc.nbCars = Number(current.value);
                    break;
                  case "Number of clients":
                    acc.nbClients = Number(current.value);
                    break;
                  default:
                    break;
                }
                return acc;
              },
              {
                staisfaction: 0,
                nbCars: 0,
                nbClients: 0,
              }
            );
            setState(newState);
          }, 500);
          return () => {
            clearTimeout(timeoutId);
          };
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: "0px",
      threshold: 0.3,
    });

    if (block) {
      observer.observe(block);
    }

    return () => {
      if (block) {
        observer.unobserve(block);
      }
    };
  }, [list]);

  return (
    <section
      className="box-cta-2 background-body overflow-hidden mt-50 ac-home-says"
      ref={blockRef}
    >
      <div
        className="bg-shape top-50 start-50 translate-middle"
        style={{
          right: "auto",
        }}
      ></div>
      <div className="container position-relative z-1 ">
        <div className="row ">
          <h3
            className={`${
              i18n.language === "ar" ? "ac--font-expo" : ""
            } text-white wow fadeInDown`}
            data-wow-duration={HOME_WOW_DURATION}
            data-wow-delay={homeWowDelay(0)}
          >
            {t("home.says.title")}
          </h3>
        </div>
        <Row className=" align-items-center py-96">
          <Col
            xxl={20}
            xl={20}
            md={24}
            lg={24}
            sm={24}
            xs={24}
            className="wow fadeIn"
            data-wow-duration={HOME_WOW_DURATION}
            data-wow-delay={homeWowDelay(1)}
          >
            <div className="row">
              <div className="col-md-4 col-4 mb-md-0 mb-4 d-flex flex-column align-items-center align-items-md-start ac--border-right">
                <div className="d-flex justify-content-center justify-content-md-start">
                  <h3 className="count text-white">
                    <Odometer
                      dir="ltr"
                      value={state.staisfaction}
                      format="(.ddd)"
                    />
                  </h3>
                  <h3 className="text-white">%</h3>
                </div>
                <div className="position-relative">
                  <h4
                    className={`${
                      i18n.language === "ar" ? "ac--font-expo" : ""
                    } text-white`}
                  >
                    {t("home.says.item1")}
                  </h4>
                </div>
              </div>
              <div className="col-md-4 col-4 mb-md-0 mb-4 d-flex flex-column align-items-center align-items-md-start ac--border-right">
                <div className="d-flex justify-content-center justify-content-md-start">
                  <h3 className="count text-white">
                    +<Odometer dir="ltr" value={state.nbCars} format="(.ddd)" />
                  </h3>
                  <h3 className="text-white">K</h3>
                </div>
                <div className="position-relative">
                  <h4
                    className={`${
                      i18n.language === "ar" ? "ac--font-expo" : ""
                    } text-white`}
                  >
                    {t("home.says.item2")}
                  </h4>
                </div>
              </div>
              <div className="col-md-4 col-4 mb-md-0 mb-4 d-flex flex-column align-items-center align-items-md-start ac--border-right">
                <div className="d-flex justify-content-center justify-content-md-start">
                  <h3 className="count text-white">
                    <Odometer
                      dir="ltr"
                      value={state.nbClients}
                      format="(.ddd)"
                    />
                  </h3>
                  <h3 className="text-white">K</h3>
                </div>
                <div className="position-relative">
                  <h4
                    className={`${
                      i18n.language === "ar" ? "ac--font-expo" : ""
                    } text-white`}
                  >
                    {t("home.says.item3")}
                  </h4>
                </div>
              </div>
            </div>
            <div className="row pt-70">
              {data &&
                data.slice(0, 3).map((item: any, i: number) => {
                  const photo =
                    item.image_1920 ||
                    item.image ||
                    item.image_url ||
                    item.avatar;
                  const rating = clampRating(item.rating);
                  return (
                    <div
                      className="col-lg-4 d-flex"
                      key={item.id ?? `testimony-${i}`}
                    >
                      <div
                      className="card-journey-big background-card hover-up ac-says-testimonial wow fadeInUp"
                      data-wow-duration={HOME_WOW_DURATION}
                      data-wow-delay={homeWowDelay(2 + i)}
                    >
                        <div className="card-info">
                          <div className="ac-says-testimonial__head">
                            <div className="ac-says-testimonial__avatar" aria-hidden>
                              {photo ? (
                                <img src={photo} alt="" loading="lazy" />
                              ) : (
                                <span className="ac-says-testimonial__initials">
                                  {initialsFromName(item.name)}
                                </span>
                              )}
                            </div>
                            <div className="ac-says-testimonial__meta">
                              <div className="card-title">
                                <p className="heading-6 neutral-1000 ac-says-testimonial__name ac--font-expo">
                                  {item.name}
                                </p>
                              </div>
                              <div
                                className="ac-says-testimonial__stars"
                                aria-label={t("home.says.ratingAria", {
                                  count: rating,
                                })}
                              >
                                {Array.from({ length: 5 }, (_, si) =>
                                  si < rating ? (
                                    <MdStar
                                      key={si}
                                      className="ac-says-testimonial__star"
                                      aria-hidden
                                    />
                                  ) : (
                                    <MdStarBorder
                                      key={si}
                                      className="ac-says-testimonial__star ac-says-testimonial__star--empty"
                                      aria-hidden
                                    />
                                  )
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="card-program">
                            <p className="ac-says-testimonial__quote ac--font-expo">
                              {item.summary}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </Col>
          <Col xxl={4} xl={4} md={0} lg={0} sm={0} xs={0}></Col>
        </Row>
      </div>
    </section>
  );
};

export default Says;
